// Purchasing screens (orders, receiving, transfers, corrections): colours, shapes and document statuses.
// Values follow the Sales overview cards; layout stays inline-styled (module Tailwind classes are not in the host build).
import React from 'react';

export const INK = '#05292A';
export const TEXT = '#222525';
export const MUTED = '#4A5353';
export const DIM = '#6A7878';
export const TEAL = '#0F7679';
export const TEAL_DARK = '#0D5759';
export const RED = '#BA0E13';
export const AMBER = '#8A5A00';
export const GREEN = '#1F6B4A';
export const CARD_BORDER = '#E5ECEC';
export const DIVIDER = '#EBF0F0';
export const CONTROL = '#C9D7D8';
export const CANVAS = '#F4F8F9';
export const TINT = '#FAFBFB';
export const ROW_ERROR = '#FDF5F5';
export const ACTIVE_BG = '#F3FAFA';
export const MONO = 'ui-monospace, Menlo, monospace';
export const CARD_SHADOW = '0 1px 2px rgba(5,41,42,0.04), 0 8px 24px rgba(5,41,42,0.05)';

export const card: React.CSSProperties = {
  background: '#FFFFFF',
  border: `1px solid ${CARD_BORDER}`,
  borderRadius: '16px',
  boxShadow: CARD_SHADOW,
};

export type Family = 'draft' | 'waiting' | 'progress' | 'done' | 'stopped';

/** A status as the pill shows it: an English label (translated where shown) and its colour family. */
export interface StatusInfo {
  label: string;
  family: Family;
}

/** One status-strip button: the status group its key filters on. */
export interface StatusGroup {
  key: string;
  label: string;
  family: Family | null;
}

export const FAMILY: Record<Family, { bg: string; fg: string }> = {
  draft: { bg: '#F2F4F3', fg: MUTED },
  waiting: { bg: '#FBF1E0', fg: AMBER },
  progress: { bg: '#E7F1F1', fg: TEAL },
  done: { bg: '#E4F1EA', fg: GREEN },
  stopped: { bg: '#F6E0E0', fg: RED },
};

/** Supplier order statuses (dbo.orders.status_id). */
export const ORDER_STATUS = {
  Draft: 1,
  SentForApproval: 2,
  Approved: 3,
  Rejected: 4,
  SentToSupplier: 5,
  Acknowledged: 6,
  PartiallyReceived: 7,
  Received: 8,
  Cancelled: 9,
} as const;

/** The strip's groups, in the order the strip shows them. */
export const STATUS_GROUPS: StatusGroup[] = [
  { key: 'all', label: 'All', family: null },
  { key: 'draft', label: 'Draft', family: 'draft' },
  { key: 'waiting', label: 'Waiting for approval', family: 'waiting' },
  { key: 'sent', label: 'Sent to supplier', family: 'progress' },
  { key: 'part', label: 'Part received', family: 'progress' },
  { key: 'received', label: 'Received', family: 'done' },
  { key: 'rejected', label: 'Rejected', family: 'stopped' },
  { key: 'cancelled', label: 'Cancelled', family: 'stopped' },
];

export const statusOf = (statusId: unknown): StatusInfo => {
  switch (Number(statusId)) {
    case ORDER_STATUS.Draft:
      return { label: 'Draft', family: 'draft' };
    case ORDER_STATUS.SentForApproval:
      return { label: 'Waiting for approval', family: 'waiting' };
    case ORDER_STATUS.Approved:
    case ORDER_STATUS.SentToSupplier:
    case ORDER_STATUS.Acknowledged:
      return { label: 'Sent to supplier', family: 'progress' };
    case ORDER_STATUS.PartiallyReceived:
      return { label: 'Part received', family: 'progress' };
    case ORDER_STATUS.Received:
      return { label: 'Received', family: 'done' };
    case ORDER_STATUS.Rejected:
      return { label: 'Rejected', family: 'stopped' };
    case ORDER_STATUS.Cancelled:
      return { label: 'Cancelled', family: 'stopped' };
    default:
      return { label: 'Draft', family: 'draft' };
  }
};

export const isSentStatus = (statusId: unknown): boolean =>
  ([ORDER_STATUS.Approved, ORDER_STATUS.SentToSupplier, ORDER_STATUS.Acknowledged, ORDER_STATUS.PartiallyReceived] as number[]).includes(Number(statusId));

/** An order can be cancelled until something is received; a draft is deleted instead (the API applies the same rule). */
export const isCancellableStatus = (statusId: unknown): boolean =>
  ([ORDER_STATUS.SentForApproval, ORDER_STATUS.Approved, ORDER_STATUS.SentToSupplier, ORDER_STATUS.Acknowledged] as number[]).includes(Number(statusId));

/** The deliveries list: orders from sent to received. */
export const DELIVERY_STATUS_GROUPS: StatusGroup[] = STATUS_GROUPS.filter((group) => ['all', 'sent', 'part', 'received'].includes(group.key));

/** Stock transfer statuses (dbo.inventory_transfers.status_id). */
export const TRANSFER_STATUS = {
  Draft: 1,
  Sent: 2,
  Received: 3,
  Rejected: 4,
  Cancelled: 5,
} as const;

export const TRANSFER_STATUS_GROUPS: StatusGroup[] = [
  { key: 'all', label: 'All', family: null },
  { key: 'draft', label: 'Draft', family: 'draft' },
  { key: 'transit', label: 'In transit', family: 'progress' },
  { key: 'received', label: 'Received', family: 'done' },
  { key: 'rejected', label: 'Rejected', family: 'stopped' },
  { key: 'cancelled', label: 'Cancelled', family: 'stopped' },
];

export const transferStatusOf = (statusId: unknown): StatusInfo => {
  switch (Number(statusId)) {
    case TRANSFER_STATUS.Sent:
      return { label: 'In transit', family: 'progress' };
    case TRANSFER_STATUS.Received:
      return { label: 'Received', family: 'done' };
    case TRANSFER_STATUS.Rejected:
      return { label: 'Rejected', family: 'stopped' };
    case TRANSFER_STATUS.Cancelled:
      return { label: 'Cancelled', family: 'stopped' };
    default:
      return { label: 'Draft', family: 'draft' };
  }
};

/** Stock count statuses (dbo.inventory_counts.status_id). */
export const COUNT_STATUS = {
  Draft: 1,
  SentForApproval: 2,
  RecountRequested: 3,
  Completed: 4,
  Cancelled: 5,
  Rejected: 6,
} as const;

/** Counting holds drafts and recounts; cancelled shows only when there are any. */
export const COUNT_STATUS_GROUPS: StatusGroup[] = [
  { key: 'all', label: 'All', family: null },
  { key: 'counting', label: 'Counting', family: 'draft' },
  { key: 'waiting', label: 'Waiting for approval', family: 'waiting' },
  { key: 'completed', label: 'Approved', family: 'done' },
  { key: 'rejected', label: 'Rejected', family: 'stopped' },
  { key: 'cancelled', label: 'Cancelled', family: 'stopped' },
];

export const countStatusOf = (statusId: unknown): StatusInfo => {
  switch (Number(statusId)) {
    case COUNT_STATUS.SentForApproval:
      return { label: 'Waiting for approval', family: 'waiting' };
    case COUNT_STATUS.RecountRequested:
      return { label: 'Recount', family: 'progress' };
    case COUNT_STATUS.Completed:
      return { label: 'Approved', family: 'done' };
    case COUNT_STATUS.Rejected:
      return { label: 'Rejected', family: 'stopped' };
    case COUNT_STATUS.Cancelled:
      return { label: 'Cancelled', family: 'stopped' };
    default:
      return { label: 'Counting', family: 'draft' };
  }
};

/** Stock correction statuses (dbo.inventory_corrections.status_id). */
export const CORRECTION_STATUS = {
  Draft: 1,
  Pending: 2,
  Approved: 3,
  Rejected: 4,
  Completed: 5,
  Cancelled: 6,
  Reversed: 7,
} as const;

/** The strip shows waiting, approved and rejected; draft, cancelled and reversed only when there are any. */
export const CORRECTION_STATUS_GROUPS: StatusGroup[] = [
  { key: 'all', label: 'All', family: null },
  { key: 'waiting', label: 'Waiting for approval', family: 'waiting' },
  { key: 'approved', label: 'Approved', family: 'done' },
  { key: 'rejected', label: 'Rejected', family: 'stopped' },
  { key: 'draft', label: 'Draft', family: 'draft' },
  { key: 'cancelled', label: 'Cancelled', family: 'stopped' },
  { key: 'reversed', label: 'Reversed', family: 'stopped' },
];

export const correctionStatusOf = (statusId: unknown): StatusInfo => {
  switch (Number(statusId)) {
    case CORRECTION_STATUS.Pending:
      return { label: 'Waiting for approval', family: 'waiting' };
    case CORRECTION_STATUS.Approved:
    case CORRECTION_STATUS.Completed:
      return { label: 'Approved', family: 'done' };
    case CORRECTION_STATUS.Rejected:
      return { label: 'Rejected', family: 'stopped' };
    case CORRECTION_STATUS.Cancelled:
      return { label: 'Cancelled', family: 'stopped' };
    case CORRECTION_STATUS.Reversed:
      return { label: 'Reversed', family: 'stopped' };
    default:
      return { label: 'Draft', family: 'draft' };
  }
};
